import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "./GenericMedicines.css";
import LoadingAnimation from "../components/LoadingAnimation";
import axiosInstance from "../api/axiosInstance";
import Swal from "sweetalert2";

const LOGIN_PATH = "/login";
const SIGNUP_PATH = "/signup-customer";

export default function PublicGenericMedicines() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const requestIdRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const normalizedSearch = search
    .replace(/[,+]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const loadProducts = useCallback(async (pageNum, searchTerm, append = false) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadError("");

    try {
      const res = await axiosInstance.get(
        `/medicine/list/?page=${pageNum}&search=${encodeURIComponent(searchTerm)}&medicine_category=generic`
      );

      // Ignore an older response if the user has already searched again.
      if (requestId !== requestIdRef.current) return;

      const results = Array.isArray(res.data?.results) ? res.data.results : [];
      setHasMore(Boolean(res.data?.next));
      setSuggestion(pageNum === 1 ? (res.data?.suggestion || "") : "");

      setProducts((previous) => {
        if (!append || pageNum === 1) return results;
        const existingIds = new Set(previous.map((item) => item.id));
        return [...previous, ...results.filter((item) => !existingIds.has(item.id))];
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Public medicine list failed:", error);
      setLoadError("Medicines could not be loaded. Please try again.");
      if (!append) setProducts([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        loadingMoreRef.current = false;
      }
    }
  }, []);

  // Initial load and page changes.
  useEffect(() => {
    if (page === 1) {
      loadProducts(1, normalizedSearch, false);
      return;
    }
    loadProducts(page, normalizedSearch, true);
  }, [page, normalizedSearch, loadProducts]);

  // Reset pagination when the search term changes.
  useEffect(() => {
    setPage(1);
  }, [normalizedSearch]);

  // Public search suggestions. This endpoint should remain read-only/public.
  useEffect(() => {
    if (!normalizedSearch) {
      setSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(
          `/medicine/suggestions/?q=${encodeURIComponent(normalizedSearch)}&medicine_category=generic`
        );
        if (!cancelled) {
          setSuggestions(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Medicine suggestions unavailable:", error);
          setSuggestions([]);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [normalizedSearch]);

  // Infinite scroll, without firing repeated page increments while a request is active.
  useEffect(() => {
    const handleScroll = () => {
      if (
        !loading &&
        hasMore &&
        !loadingMoreRef.current &&
        window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 160
      ) {
        loadingMoreRef.current = true;
        setPage((current) => current + 1);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore]);

  const promptLogin = async () => {
    const result = await Swal.fire({
      title: "Login to continue",
      text: "Please log in or create an account to add medicines to your cart.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Login",
      cancelButtonText: "Sign Up",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#16a34a",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      navigate(LOGIN_PATH, { state: { from: "/generic-medicines" } });
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      navigate(SIGNUP_PATH, { state: { from: "/generic-medicines" } });
    }
  };

  const handleAddToCart = (product) => {
    if (!product || Number(product.stock || 0) <= 0) return;
    // Public page deliberately does not call cart endpoints.
    promptLogin();
  };

  const chooseSuggestion = (item) => {
    setSearch(item.name || "");
    setSuggestions([]);
    setShowSuggestions(false);
    setPage(1);
  };

  if (loading && products.length === 0 && !loadError) {
    return <LoadingAnimation />;
  }

  return (
    <main className="shop-container">
      <section style={styles.searchWrapper}>
        <div style={styles.trustLine}>
          <span>✓ Same Salt Formula</span>
          <span>✓ Trusted Quality</span>
          <span>✓ Save up to 70%</span>
        </div>

        <div style={styles.searchArea}>
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setShowSuggestions(false);
                setPage(1);
              }
            }}
            placeholder="Search generic medicines..."
            aria-label="Search generic medicines"
            style={styles.searchInput}
          />

          {showSuggestions && suggestions.length > 0 && (
            <div style={styles.suggestions}>
              {suggestions.map((item) => (
                <button
                  type="button"
                  key={item.id ?? item.name}
                  style={styles.suggestionItem}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => chooseSuggestion(item)}
                >
                  <strong>{item.name}</strong>
                  {item.salt_composition && (
                    <span style={styles.suggestionSalt}>{item.salt_composition}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {suggestion && normalizedSearch.toLowerCase() !== suggestion.toLowerCase() && (
          <p style={styles.didYouMean}>
            Did you mean{" "}
            <button type="button" onClick={() => setSearch(suggestion)} style={styles.textButton}>
              {suggestion}
            </button>
            ?
          </p>
        )}

        <p style={styles.searchHint}>
          💊 <strong>For best results, search by salt name</strong>
          <br />
          Example: Paracetamol, Azithromycin, Metformin
        </p>
      </section>

      {loadError && (
        <div role="alert" style={styles.errorBox}>
          <p>{loadError}</p>
          <button
            type="button"
            style={styles.retryButton}
            onClick={() => {
              setPage(1);
              loadProducts(1, normalizedSearch, false);
            }}
          >
            Try again
          </button>
        </div>
      )}

      {!loadError && products.length === 0 && !loading && (
        <p style={styles.emptyState}>No medicines found. Try another search.</p>
      )}

      <section className="product-grid" aria-label="Generic medicines">
        {products.map((product) => {
          const price = Number(product.selling_price || product.price || 0);
          const mrp = Number(product.mrp || product.price || 0);
          const stock = Number(product.stock || 0);
          const discount = mrp > price && mrp > 0
            ? Math.round(((mrp - price) / mrp) * 100)
            : 0;
          const images = Array.isArray(product.images) && product.images.length
            ? product.images.filter(Boolean)
            : product.image
              ? [product.image]
              : ["/no-image.png"];

          return (
            <article key={product.id} style={styles.productCard}>
              <div style={styles.productInfo}>
                <h2 style={styles.productName}>{product.name}</h2>

                {product.salt_composition && (
                  <div style={styles.saltBadge}>🧪 {product.salt_composition}</div>
                )}

                {product.company && (
                  <div style={styles.company}>by <strong>{product.company}</strong></div>
                )}
              </div>

              <div style={styles.imageFrame}>
                <PhotoProvider maskClosable>
                  <Swiper
                    spaceBetween={0}
                    slidesPerView={1}
                    pagination={{ clickable: true }}
                    modules={[Pagination]}
                    style={{ width: "100%", height: "100%" }}
                  >
                    {images.map((imageUrl, index) => (
                      <SwiperSlide key={`${product.id}-${index}`}>
                        <div style={styles.imageSlide}>
                          <PhotoView src={imageUrl}>
                            <img
                              src={imageUrl}
                              alt={product.name || "Medicine"}
                              loading="lazy"
                              onError={(event) => {
                                if (!event.currentTarget.dataset.fallback) {
                                  event.currentTarget.dataset.fallback = "true";
                                  event.currentTarget.src = "/no-image.png";
                                }
                              }}
                              style={styles.productImage}
                            />
                          </PhotoView>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </PhotoProvider>
              </div>

              <p style={styles.zoomHint}>Tap image to zoom</p>

              <div style={styles.priceBlock}>
                {mrp > price && <del style={styles.mrp}>₹{mrp.toFixed(2)}</del>}
                <div style={styles.price}>₹{price.toFixed(2)}</div>
                {discount > 0 && <div style={styles.discount}>{discount}% OFF</div>}
                {stock <= 0 && <div style={styles.outOfStock}>Out of Stock</div>}
              </div>

              <button
                type="button"
                onClick={() => handleAddToCart(product)}
                disabled={stock <= 0}
                style={{
                  ...styles.addButton,
                  ...(stock <= 0 ? styles.disabledButton : {}),
                }}
              >
                {stock <= 0 ? "Out of Stock" : "Add to Cart"}
              </button>
            </article>
          );
        })}
      </section>

      {loading && products.length > 0 && (
        <div style={styles.loadingMore}><LoadingAnimation small /></div>
      )}
      {!loading && hasMore && products.length > 0 && (
        <button
          type="button"
          style={styles.loadMoreButton}
          onClick={() => {
            if (!loadingMoreRef.current) {
              loadingMoreRef.current = true;
              setPage((current) => current + 1);
            }
          }}
        >
          Load more medicines
        </button>
      )}
    </main>
  );
}

const styles = {
  searchWrapper: {
    maxWidth: 560,
    margin: "22px auto 26px",
    padding: "0 12px",
    position: "relative",
    boxSizing: "border-box",
  },
  trustLine: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "8px 18px",
    marginBottom: 16,
    color: "#16803d",
    fontSize: 13,
    fontWeight: 650,
    textAlign: "center",
  },
  searchArea: {
    position: "relative",
    width: "100%",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 15px",
    borderRadius: 11,
    border: "1px solid #d1d5db",
    background: "#fff",
    fontSize: 14,
    outlineColor: "#2563eb",
  },
  suggestions: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    width: "100%",
    boxSizing: "border-box",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    zIndex: 1000,
    maxHeight: "40vh",
    overflowY: "auto",
    boxShadow: "0 8px 20px rgba(15,23,42,0.12)",
  },
  suggestionItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 3,
    width: "100%",
    padding: "11px 13px",
    border: 0,
    borderBottom: "1px solid #f1f5f9",
    background: "#fff",
    textAlign: "left",
    cursor: "pointer",
  },
  suggestionSalt: {
    color: "#64748b",
    fontSize: 12,
  },
  didYouMean: {
    margin: "10px 0 0",
    textAlign: "center",
    fontSize: 13,
    color: "#475569",
  },
  textButton: {
    border: 0,
    padding: 0,
    background: "transparent",
    color: "#2563eb",
    fontWeight: 700,
    cursor: "pointer",
  },
  searchHint: {
    margin: "10px 0 0",
    textAlign: "center",
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.65,
  },
  errorBox: {
    maxWidth: 560,
    margin: "20px auto",
    padding: 16,
    borderRadius: 12,
    background: "#fff1f2",
    color: "#9f1239",
    textAlign: "center",
  },
  retryButton: {
    padding: "9px 15px",
    border: 0,
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  emptyState: {
    textAlign: "center",
    padding: 24,
    color: "#64748b",
  },
  productCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minWidth: 0,
    minHeight: 470,
    padding: 13,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
    boxSizing: "border-box",
    boxShadow: "0 3px 10px rgba(15,23,42,0.04)",
  },
  productInfo: {
    textAlign: "center",
    marginBottom: 8,
  },
  productName: {
    margin: "0 0 7px",
    color: "#1f2937",
    fontSize: 14,
    lineHeight: 1.4,
    fontWeight: 750,
    minHeight: 39,
  },
  saltBadge: {
    display: "inline-block",
    padding: "4px 7px",
    marginBottom: 5,
    borderRadius: 6,
    background: "#eef2ff",
    color: "#3730a3",
    fontSize: 11,
    lineHeight: 1.45,
  },
  company: {
    color: "#64748b",
    fontSize: 11,
    marginTop: 2,
  },
  imageFrame: {
    width: "100%",
    height: 175,
    overflow: "hidden",
    position: "relative",
    textAlign: "center",
  },
  imageSlide: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    cursor: "zoom-in",
  },
  zoomHint: {
    margin: "5px 0 9px",
    color: "#94a3b8",
    textAlign: "center",
    fontSize: 10,
  },
  priceBlock: {
    textAlign: "center",
    marginTop: "auto",
    paddingTop: 7,
    minHeight: 65,
  },
  mrp: {
    display: "block",
    color: "#dc2626",
    fontSize: 12,
  },
  price: {
    color: "#15803d",
    fontSize: 17,
    fontWeight: 700,
  },
  discount: {
    color: "#ea580c",
    fontSize: 12,
    fontWeight: 600,
  },
  outOfStock: {
    color: "#dc2626",
    fontSize: 12,
    fontWeight: 700,
  },
  addButton: {
    width: "100%",
    marginTop: 10,
    padding: "10px 8px",
    border: 0,
    borderRadius: 7,
    background: "#15803d",
    color: "#fff",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  disabledButton: {
    background: "#e5e7eb",
    color: "#64748b",
    cursor: "not-allowed",
  },
  loadingMore: {
    display: "flex",
    justifyContent: "center",
    padding: 18,
  },
  loadMoreButton: {
    display: "block",
    margin: "20px auto",
    padding: "11px 20px",
    border: 0,
    borderRadius: 9,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
};
